import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaTimes } from "react-icons/fa";
import Navbar from "../components/NavBar";
import { useAppDispatch, useAppSelector } from "../store/store";
import PostAction from "../store/post/action";
import AppAction from "../store/app/action";
import FirebaseStorage from "../utils/firestore/storage";
import Clothes from "../models/clothes/clothes.model";
import { ClothesTypes, ClothesSize, ClothesColors, ClothesGender } from "../models/clothes/clothes.model";
import type { AppTransport } from "../models/app/app.model";
import { categoryLabels, categoryList } from "../utils/categoryData";
import { sizeOptions, colorOptions, colorLabels, colorHex } from "../utils/filterOptions";
import { resizeImageFile, DEFAULT_MAX_BYTES, MAX_ORIGINAL_BYTES } from "../utils/imageResize";

const MAX_IMAGES = 3;
const MAX_IMAGE_WIDTH = 1024;
const MAX_IMAGE_HEIGHT = 1024;

function hexToRgba(hex: string, alpha: number): string {
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

const MyPostAddPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((s) => s.auth);
    const { addPostSuccess, isLoading, error } = useAppSelector((s) => s.post);
    const { data: appData } = useAppSelector((s) => s.app);
    const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [type, setType] = useState<ClothesTypes>("dress");
    const [sizes, setSizes] = useState<ClothesSize[]>(["M"]);
    const [colors, setColors] = useState<ClothesColors[]>(["black"]);
    const [gender, setGender] = useState<ClothesGender>("female");
    const [sellPrice, setSellPrice] = useState(0);
    const [rentPrice, setRentPrice] = useState(0);
    const [isFree, setIsFree] = useState(false);
    const [imageFiles, setImageFiles] = useState<(File | null)[]>([null, null, null]);
    const [imageResizingIndex, setImageResizingIndex] = useState<number | null>(null);
    const [imageError, setImageError] = useState<string | null>(null);
    const [imagesUploading, setImagesUploading] = useState(false);

    const handleImageSelect = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !file.type.startsWith("image/")) return;
        e.target.value = "";
        setImageError(null);
        setImageResizingIndex(index);
        try {
            if (file.size > MAX_ORIGINAL_BYTES) {
                setImageError(`Fotoja është shumë e madhe (maks. ${Math.round(MAX_ORIGINAL_BYTES / 1024 / 1024)} MB). Zgjidhni një foto më të vogël.`);
                setImageResizingIndex(null);
                return;
            }
            const resized = await resizeImageFile(file, MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT, DEFAULT_MAX_BYTES);
            if (!resized) {
                setImageError("Fotoja është shumë e madhe. Zgjidhni një foto më të vogël.");
                setImageResizingIndex(null);
                return;
            }
            setImageFiles((prev) => {
                const next = [...prev];
                next[index] = resized;
                return next;
            });
        } catch {
            setImageError("Një gabim u paraqit. Provoni një foto tjetër.");
            setImageFiles((prev) => {
                const next = [...prev];
                next[index] = file;
                return next;
            });
        } finally {
            setImageResizingIndex(null);
        }
    };

    const handleImageRemove = (index: number) => {
        setImageError(null);
        setImageFiles((prev) => {
            const next = [...prev];
            next[index] = null;
            return next;
        });
    };

    const handleSubmit = async () => {
        if (!user?.id || !name.trim()) return;
        const files = imageFiles.filter((f): f is File => f !== null);
        if (files.length === 0) return;
        const postId = crypto.randomUUID?.() ?? `post_${Date.now()}`;
        setImagesUploading(true);
        try {
            const imageUrls = await FirebaseStorage.getImagesUrl("posts", user.id, files, postId);
            console.log("imageUrls", imageUrls);
            const post: Clothes = {
                id: postId,
                name: name.trim(),
                description: description.trim(),
                type,
                size: sizes,
                colors,
                gender,
                images: imageUrls,
                prices: {
                    sellPrice: isFree ? 0 : sellPrice,
                    rentPrice,
                    isFree,
                },
                seller_id: user.id,
                isTransportXKIncluded: true,
                isTransportMKIncluded: true,
                isTransportALIncluded: true,
                created_at: Date.now(),
                updated_at: null,
                created_from: "web",
                updated_from: null,
            };
            PostAction.addPost(user, post, dispatch);
        } catch (err) {
            console.error("Upload error:", err);
        } finally {
            setImagesUploading(false);
        }
    };

    const hasAtLeastOneImage = imageFiles.some((f) => f !== null);

    const previewUrls = useMemo(() =>
        imageFiles.map((f) => (f ? URL.createObjectURL(f) : null)),
        [imageFiles]
    );
    useEffect(() => {
        return () => previewUrls.forEach((u) => u && URL.revokeObjectURL(u));
    }, [previewUrls]);

    useEffect(() => {
        AppAction.getAppData(dispatch);
    }, [dispatch]);

    const transportList: AppTransport[] = appData?.transport?.length
        ? appData.transport
        : [
            { country: "Kosovë", country_code: "XK", fee: 2 },
            { country: "Maqedoni", country_code: "MK", fee: 3 },
            { country: "Shqipëri", country_code: "AL", fee: 5 },
        ];

    const priceDisplay = isFree ? 0 : sellPrice;
    const rentPriceDisplay = rentPrice;
    const transportFeeForRent = (fee: number) => 2 * fee;

    const toggleSize = (s: ClothesSize) => {
        setSizes((prev) => {
            const next = prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s];
            return next.length ? next : ["M"];
        });
    };

    const toggleColor = (c: ClothesColors) => {
        setColors((prev) =>
            prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
        );
    };

    if (addPostSuccess) {
        PostAction.clearState(dispatch);
        navigate("/my-posts");
        return null;
    }

    return (
        <div className="min-h-screen bg-[#f8f2ed] font-manrope">
            <Navbar />
            <div className="max-w-xl mx-auto px-4 py-6">
                <h1 className="text-2xl font-semibold text-[#704e37] mb-4">Shto shpallje</h1>
                <div className="bg-white rounded-xl shadow p-4 space-y-3">
                    <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Fotot (mund të postoni 1 deri në 3 foto)</label>
                        <div className="flex gap-3 flex-wrap">
                            {[0, 1, 2].map((index) => (
                                <div key={index} className="relative">
                                    <input
                                        ref={(el) => { fileInputRefs.current[index] = el; }}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => handleImageSelect(index, e)}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRefs.current[index]?.click()}
                                        disabled={imageResizingIndex !== null}
                                        className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden hover:border-[#704e37] hover:bg-[#f8f2ed] transition disabled:opacity-70 relative"
                                    >
                                        {previewUrls[index] ? (
                                            <img
                                                src={previewUrls[index]!}
                                                alt={`Preview ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <FaPlus className="text-2xl text-[#704e37]" />
                                        )}
                                        {imageResizingIndex === index && (
                                            <span className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center text-white text-xs">
                                                Duke përshtatur...
                                            </span>
                                        )}
                                    </button>
                                    {imageFiles[index] && (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); handleImageRemove(index); }}
                                            className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                                            aria-label="Hiq foton"
                                        >
                                            <FaTimes className="text-xs" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        {imageError && (
                            <p className="text-xs text-red-600 mt-1">{imageError}</p>
                        )}
                        {!hasAtLeastOneImage && !imageError && (
                            <p className="text-xs text-amber-600 mt-1">Zgjidhni të paktën një foto</p>
                        )}
                    </div>
                    <input placeholder="Emri" value={name} onChange={(e) => setName(e.target.value)} className="w-full border rounded px-3 py-2" />
                    <textarea placeholder="Përshkrimi" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border rounded px-3 py-2" rows={3} />
                    <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Lloji</label>
                        <select value={type} onChange={(e) => setType(e.target.value as ClothesTypes)} className="w-full border rounded px-3 py-2">
                            {categoryList.map((t) => (
                                <option key={t} value={t}>{categoryLabels[t]}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Madhësitë</label>
                        <div className="flex flex-wrap gap-2">
                            {sizeOptions.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => toggleSize(s)}
                                    className={`min-w-[2.5rem] px-3 py-2 rounded-lg border-2 font-medium transition ${sizes.includes(s) ? "bg-[#704e37] border-[#704e37] text-white" : "bg-white border-gray-200 text-gray-700 hover:border-gray-400"}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Gjinia</label>
                        <select value={gender} onChange={(e) => setGender(e.target.value as ClothesGender)} className="w-full border rounded px-3 py-2">
                            <option value="female">Femër</option>
                            <option value="male">Mashkull</option>
                            <option value="both">Të dyja</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Çmimi shitje (€)</label>
                        <input type="number" min={0} value={sellPrice || ""} onChange={(e) => setSellPrice(Number(e.target.value) || 0)} className="w-full border rounded px-3 py-2" />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Çmimi qira (€)</label>
                        <input type="number" min={0} value={rentPrice || ""} onChange={(e) => setRentPrice(Number(e.target.value) || 0)} className="w-full border rounded px-3 py-2" />
                    </div>
                    <div className="flex items-center gap-2">
                        <input type="checkbox" id="free" checked={isFree} onChange={(e) => setIsFree(e.target.checked)} />
                        <label htmlFor="free">Falas</label>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Ngjyra</label>
                        <div className="flex flex-wrap gap-1.5">
                            {colorOptions.map((c) => {
                                const selected = colors.includes(c);
                                return (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => toggleColor(c)}
                                    title={colorLabels[c]}
                                    className={`w-8 h-8 rounded-full border-2 transition ${selected ? "border-[#704e37] ring-2 ring-[#704e37]/30" : "border-gray-200 hover:border-gray-400"}`}
                                    style={{
                                        backgroundColor: selected ? colorHex[c] : hexToRgba(colorHex[c], 0.7),
                                    }}
                                />
                                );
                            })}
                        </div>
                    </div>

                    {/* <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Transporti</label>
                        <p className="text-xs text-gray-500 mb-2">Çmimi që keni shënuar përfshin transportin. Për çdo vend ku ofroni transport, blerësi paguan këtë çmim; ju merrni çmimin minus koston e transportit.</p>
                        <div className="space-y-3">
                            {transportList.map((t) => {
                                const buyerPaysSell = priceDisplay;
                                const buyerPaysRent = rentPriceDisplay;
                                const feeRent = transportFeeForRent(t.fee);
                                const sellerReceivesSell = Math.max(0, buyerPaysSell - t.fee);
                                const sellerReceivesRent = Math.max(0, buyerPaysRent - feeRent);
                                const hasSellPrice = !isFree && sellPrice > 0;
                                const hasRentPrice = rentPrice > 0;
                                return (
                                    <div
                                        key={t.country_code}
                                        className="flex flex-col gap-1 p-3 rounded-lg border border-[#704e37]/30 bg-[#f8f2ed]/50"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-800">{t.country}</span>
                                            <span className="text-xs text-gray-500">(transport: {t.fee} €)</span>
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            {isFree ? (
                                                <span>Falas. Kostoja e transportit ({t.fee} €) paguhet nga ju.</span>
                                            ) : (
                                                <>
                                                    {hasSellPrice && (
                                                        <p>Shitje: blerësi paguan <strong>{buyerPaysSell} €</strong>, ju merrni <strong>{sellerReceivesSell} €</strong> (pas {t.fee} € transport).</p>
                                                    )}
                                                    {hasRentPrice && (
                                                        <p>Qira: blerësi paguan <strong>{buyerPaysRent} €</strong>, ju merrni <strong>{sellerReceivesRent} €</strong> (pas {feeRent} € transporti i kthimit).</p>
                                                    )}
                                                    {!hasSellPrice && !hasRentPrice && (
                                                        <span>Vendosni çmimin shitje ose qira për të parë llogaritjen.</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div> */}
                </div>
                {error && <p className="text-red-500 mt-2">{error}</p>}
                <div className="mt-4 flex gap-2">
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading || imagesUploading || !name.trim() || !hasAtLeastOneImage}
                        className="flex-1 py-3 rounded-lg bg-[#704e37] text-white font-medium disabled:opacity-50"
                    >
                        {imagesUploading ? "Duke ngarkuar fotot..." : isLoading ? "Duke ruajtur..." : "Ruaj"}
                    </button>
                    <button onClick={() => navigate("/my-posts")} className="px-4 py-3 rounded-lg border border-gray-300">Anulo</button>
                </div>
            </div>
        </div>
    );
};

export default MyPostAddPage;
